import { computeBrandTokens, isPlainObject } from '@hub/common';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';
import {
  LS_BRAND_DRAFT_KEY,
  LS_BRAND_DRAFT_LOGO_KEY,
} from 'features/admin/brands/constants';
import {
  checkBrandDraft,
  parseBrandDraft,
  type BrandDraft,
  type BrandField,
} from 'features/admin/brands/logic/brandChecks';
import { readJsonFromLocalStorage } from 'features/store/localStorage';
import { createBrandTheme } from 'features/theme/theme';
import type { BrandInputs } from '@hub/common';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'features/store/store';

const storedDraft = readJsonFromLocalStorage<unknown>(LS_BRAND_DRAFT_KEY, {});
const { logo: _ignored, ...storedFields }: BrandDraft = isPlainObject(
  storedDraft
)
  ? storedDraft
  : {};
const storedLogo = localStorage.getItem(LS_BRAND_DRAFT_LOGO_KEY);
const initialState: BrandDraft = storedLogo
  ? { ...storedFields, logo: storedLogo }
  : storedFields;

const brandDraftSlice = createSlice({
  name: 'brandDraft',
  initialState,
  reducers: {
    brandDraftSetValue: (
      state,
      action: PayloadAction<{ field: BrandField; value: string }>
    ) => {
      const { field, value } = action.payload;
      const { [field]: _removed, ...rest } = state;
      return value === '' ? rest : { ...rest, [field]: value };
    },
    brandDraftSetLogo: (state, action: PayloadAction<string | undefined>) => {
      const { logo: _removed, ...rest } = state;
      return action.payload ? { ...rest, logo: action.payload } : rest;
    },
    brandDraftReplace: (_state, action: PayloadAction<BrandDraft>) =>
      action.payload,
    brandDraftClear: () => ({}),
  },
});

export const {
  brandDraftSetValue,
  brandDraftSetLogo,
  brandDraftReplace,
  brandDraftClear,
} = brandDraftSlice.actions;
export const brandDraftReducer = brandDraftSlice.reducer;

export const selectBrandDraft = (state: RootState) => state.brandDraft;
export const selectBrandDraftCheck = createSelector(
  [selectBrandDraft],
  checkBrandDraft
);
export const selectBrandDraftHasErrors = createSelector(
  [selectBrandDraftCheck],
  check => Object.keys(check.errors).length > 0
);

/** The cleaned inputs; keep their reference while only other text changes. */
const selectBrandDraftInputs = createSelector(
  [selectBrandDraft],
  draft => parseBrandDraft(draft).inputs,
  {
    memoizeOptions: { resultEqualityCheck: sameInputs },
  }
);

function sameInputs(a: BrandInputs, b: BrandInputs) {
  return a.borderRadius === b.borderRadius && shallowEqual(a.colors, b.colors);
}

/** The light participant theme of the draft, from the tokens its brand file
 *  would hold. */
export const selectBrandDraftPreviewTheme = createSelector(
  [selectBrandDraftInputs],
  inputs => createBrandTheme(computeBrandTokens(inputs))
);
